// Types and session/profile fetch helpers for useAuthState.
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { OrgRoleEntry } from "./authStateRoles";
import { coerceHasSalesFloor } from "@/lib/fulfillment/salesFloorCapability";
import { coerceServicesEnabled } from "@/lib/service/serviceOperationalCapability";
import { coercePersonalAssistantEnabled } from "@/lib/personalAssistant/personalAssistantCapability";

export type { OrgRoleEntry };

export type AccessStatus = "pending" | "approved" | "blocked" | "unknown";

/** DB enums / legacy rows sometimes use different casing or spacing than our union. */
export function normalizeAccessStatus(raw: unknown): AccessStatus {
  if (raw == null) return "unknown";
  const s = String(raw).trim().toLowerCase();
  if (s === "approved") return "approved";
  if (s === "pending") return "pending";
  if (s === "blocked") return "blocked";
  return "unknown";
}

export type AuthProfile = {
  id: string;
  email: string | null;
  full_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  personal_phone: string | null;
  work_phone: string | null;
  email_domain: string | null;
  access_status: AccessStatus;
  primary_organization_id: string | null;
  active_location_id: string | null;
};

/**
 * Organization-scoped role from organization_members, or "unassigned" when
 * user has no membership.
 *
 * Members fall into two tiers (see docs/design/role_taxonomy.md §3 + §4):
 *
 *  - **Umbrella tokens** (the original v1.0 set): `owner` / `admin` /
 *    `sales` / `trainer` / `marketing` / `ads_guidance_editor` / `dev` /
 *    `manufacturing`. These survive the carve unchanged and continue to
 *    satisfy every gate they pass today.
 *  - **Carved sub-role tokens** (2026-06-15, role-taxonomy spec): nine
 *    finer-grained tokens added so the Order Intake bucket move can
 *    gate role-scoped panel variants without re-touching the role-list
 *    constants. None of the sub-roles SUBSTITUTE for the umbrellas — a
 *    `mfg_accepter` user does NOT see the Manufacturing bucket; a
 *    `sales_rep` user does NOT see all Sales surfaces a `sales`-tier
 *    user gets. The carve is a one-way implication: every umbrella
 *    token holder passes the matching sub-role gate (per §5.1 of the
 *    spec), but holding only a sub-role does NOT promote the user to
 *    its umbrella.
 *
 * Application-layer additive only — `user_roles.role` stays free-form
 * text; no DB enum / CHECK-constraint change ships with the carve.
 */
export type OrgRole =
  | "owner"
  | "admin"
  | "sales"
  | "trainer"
  | "marketing"
  | "ads_guidance_editor"
  | "dev"
  | "manufacturing"
  // Shop-floor plant ACL (inventory_host_replacement_plan.md §5 / R1d).
  | "manufacturing_plant"
  | "manufacturing_plant_supervisor"
  | "manufacturing_plant_manager"
  | "manufacturing_purchasing"
  | "mfg_qc"
  | "mfg_final_qc"
  | "manufacturing_repair_technician"
  // Carved sub-roles per docs/design/role_taxonomy.md §4 (2026-06-15).
  | "sales_rep"
  | "sales_manager"
  | "training_author"
  | "mfg_accepter"
  | "production_operator"
  | "ops_reviewer"
  | "logistics_coordinator"
  | "driver"
  | "third_party_hauler_subcoordinator"
  | "unassigned";

export type ActiveMembership = {
  organization_id: string;
  role: Exclude<OrgRole, "unassigned">;
};

export type PlatformRole =
  | "platform_owner"
  | "platform_admin"
  | "platform_dev"
  | "platform_trainer"
  | null;

type SharedAuthFields = {
  activeMembership: ActiveMembership | null;
  role: OrgRole;
  platformRole: PlatformRole;
  allRoles: string[];
  accessibleLocationIds: string[];
  /** Distinct org ids from org-scoped user_roles rows (for workspace org validation). */
  memberOrganizationIds: string[];
  /** Org-scoped role rows (org_id + role). Excludes platform_* rows by convention. */
  orgRoles: OrgRoleEntry[];
  /** Identity-level platform_* role strings (any precedence). `platformRole` is the highest of these. */
  platformRoles: string[];
  /**
   * `organizations.has_sales_floor` for the active org (Fulfillment Slice 3).
   * Defaults `false` until the org row resolves so consumers fail closed
   * during the auth-loading window. Drives the Fulfillment bucket nav-
   * visibility gate (replaces the Slice-1 `VITE_FULFILLMENT_PILOT` env
   * flag) and the daily-checklist intake-nag enable. Per
   * docs/planning/sales_fulfillment_bucket.md §3.
   */
  hasSalesFloor: boolean;
  /**
   * `organizations.services_enabled` for the active org (Service v1.0).
   * Defaults `false` while loading (fail closed). Gates operational Service
   * bucket modes + dispatch APIs — not offerings catalog. Per
   * docs/planning/service_bucket.md §5.4.
   */
  servicesEnabled: boolean;
  /**
   * `organizations.personal_assistant_enabled` for the active org (PA-1a).
   * Defaults `false` while loading (fail closed). Gates Personal Assistant
   * mode shell — not individual tool execution. Per
   * docs/planning/personal_assistant_mode_plan.md §3.1.
   */
  personalAssistantEnabled: boolean;
};

export type AuthState =
  | { status: "no-supabase"; session: null; profile: null; accessStatus: AccessStatus } & SharedAuthFields
  | { status: "idle"; session: null; profile: null; accessStatus: AccessStatus } & SharedAuthFields
  | { status: "loading"; session: Session | null; profile: AuthProfile | null; accessStatus: AccessStatus } & SharedAuthFields
  | { status: "ready"; session: Session | null; profile: AuthProfile | null; accessStatus: AccessStatus } & SharedAuthFields
  | { status: "error"; session: Session | null; profile: AuthProfile | null; accessStatus: AccessStatus; error: string } & SharedAuthFields;

export const defaultAuthState: SharedAuthFields = {
  activeMembership: null,
  role: "unassigned",
  platformRole: null,
  allRoles: [],
  accessibleLocationIds: [],
  memberOrganizationIds: [],
  orgRoles: [],
  platformRoles: [],
  hasSalesFloor: false,
  servicesEnabled: false,
  personalAssistantEnabled: false,
};

export type UseAuthStateResult = AuthState & {
  refreshProfile: () => void;
};

function normalizeLocationIdList(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return Array.from(
    new Set(
      values
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter((value) => value.length > 0)
    )
  );
}

export async function loadAccessibleLocationIds(
  client: NonNullable<typeof supabase>,
  userId: string
): Promise<string[]> {
  const rpcResult = await client.rpc("get_my_locations");
  const rpcIds = normalizeLocationIdList(rpcResult.data);
  if (!rpcResult.error && rpcIds.length > 0) return rpcIds;

  const { data: locationConfigRows, error: locationConfigError } = await client
    .from("location_members")
    .select("location_config_id")
    .eq("user_id", userId)
    .eq("is_active", true);
  if (!locationConfigError) {
    const ids = normalizeLocationIdList((locationConfigRows ?? []).map((row) => row.location_config_id));
    if (ids.length > 0) return ids;
  }

  return rpcIds;
}

/**
 * Loads Fulfillment + Service capability flags for the active org in one
 * round-trip. Returns fail-closed `false` for each flag on error.
 */
export async function loadActiveOrgCapabilityFlags(
  client: NonNullable<typeof supabase>,
  organizationId: string | null
): Promise<{
  hasSalesFloor: boolean;
  servicesEnabled: boolean;
  personalAssistantEnabled: boolean;
}> {
  if (!organizationId) {
    return { hasSalesFloor: false, servicesEnabled: false, personalAssistantEnabled: false };
  }
  try {
    const { data, error } = await client
      .from("organizations")
      .select("has_sales_floor, services_enabled, personal_assistant_enabled")
      .eq("id", organizationId)
      .maybeSingle();
    if (error) {
      return { hasSalesFloor: false, servicesEnabled: false, personalAssistantEnabled: false };
    }
    const row = data as {
      has_sales_floor?: unknown;
      services_enabled?: unknown;
      personal_assistant_enabled?: unknown;
    } | null;
    return {
      hasSalesFloor: coerceHasSalesFloor(row?.has_sales_floor),
      servicesEnabled: coerceServicesEnabled(row?.services_enabled),
      personalAssistantEnabled: coercePersonalAssistantEnabled(row?.personal_assistant_enabled),
    };
  } catch {
    return { hasSalesFloor: false, servicesEnabled: false, personalAssistantEnabled: false };
  }
}

/**
 * Loads `organizations.has_sales_floor` for the active org (Fulfillment
 * Slice 3 capability gate). Returns `false` on any error or missing org —
 * the consumer treats unknown as "not visible" (fail closed). Pure-ish
 * (one Supabase select), exposed here so useAuthState can fire it in
 * parallel with `loadAccessibleLocationIds`.
 */
export async function loadActiveOrgHasSalesFloor(
  client: NonNullable<typeof supabase>,
  organizationId: string | null
): Promise<boolean> {
  const flags = await loadActiveOrgCapabilityFlags(client, organizationId);
  return flags.hasSalesFloor;
}

export function firstNonEmptyString(...values: Array<unknown>) {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
}

export function authMetadataString(session: Session, key: string) {
  const raw = (session.user.user_metadata as Record<string, unknown> | undefined)?.[key];
  return typeof raw === "string" ? raw.trim() || null : null;
}

export async function syncLinkedExternalContacts(session: Session): Promise<void> {
  const token = session.access_token;
  if (!token) return;
  try {
    await fetch("/api/contacts/sync-linked-external", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.warn("[auth] linked external sync failed", error);
  }
}
